import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Box, Container, Stack, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Breadcrumbs, Button, Datepicker, GridContainer, GridItem } from '../../../components/shared';
import { routeConstants } from '../../../constants/routeConstants';
import BREADCRUMB_NAMES from '../../../constants/breadcrumbNames';
import { dateFormatForApi } from '../../../utils/common-methods';
import { FILTER_VALUES } from '../../../constants/FilterConstants';
import { setExchangeRatesFilters } from '../../../redux-slice/exchangeRatesFilters';
import { useFetchCurrencyLayerTimeframeMutation } from '../../../services/ExchangeRate/ExchangeRateService';
import useStyles from '../UpdateCurrencyRate/style';

const INITIAL_ERRORS = {
  startDate: '',
  endDate: '',
  api: '',
};

const AddUpdateCurrencyRate = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { exchangeRates } = useSelector((state) => state.exchangeRatesFilters || {});

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [fetchCurrencyLayerTimeframe, { isLoading: isApplying }] = useFetchCurrencyLayerTimeframeMutation();
  const maxSelectableDate = useMemo(() => dayjs(), []);
  const minSelectableDate = useMemo(() => dayjs().subtract(2, 'month'), []);

  const {
    SETTINGS: { SETTINGS, CURRENCY_RATES, UPDATE_CURRENCY_RATES },
  } = BREADCRUMB_NAMES;

  const breadcrumbData = useMemo(
    () => [
      { text: SETTINGS, redirection: routeConstants.SETTING_ROUTE },
      {
        text: CURRENCY_RATES,
        redirection: `${routeConstants.SETTING_ROUTE}/${routeConstants.CURRENCY_RATES_EXCHANGE_ROUTE}`,
      },
      { text: UPDATE_CURRENCY_RATES },
    ],
    [],
  );

  const validate = () => {
    const nextErrors = { ...INITIAL_ERRORS };

    if (!startDate || !dayjs(startDate).isValid()) {
      nextErrors.startDate = 'Start date is required in YYYY-MM-DD format';
    }

    if (!endDate || !dayjs(endDate).isValid()) {
      nextErrors.endDate = 'End date is required in YYYY-MM-DD format';
    }

    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
      nextErrors.endDate = 'End date must be same as or after start date';
    }

    setErrors(nextErrors);
    return Object.values(nextErrors).every((message) => !message);
  };

  const onCancel = () => {
    navigate(`/${routeConstants.SETTING_ROUTE}/${routeConstants.CURRENCY_RATES_EXCHANGE_ROUTE}`);
  };

  const onApply = async () => {
    if (!validate()) return;

    const nextFrom = dateFormatForApi(startDate);
    const nextTo = dateFormatForApi(endDate);

    try {
      await fetchCurrencyLayerTimeframe({
        fromDate: nextFrom,
        toDate: nextTo,
      }).unwrap();
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        api: error?.data?.message || 'Failed to fetch currency-layer timeframe data.',
      }));
      return;
    }

    // Set fetched timeframe range in a dedicated key — separate from the filter drawer's DATE_RANGE.
    dispatch(
      setExchangeRatesFilters({
        ...(exchangeRates || {}),
        [FILTER_VALUES.FETCHED_TIMEFRAME_RANGE]: { from: nextFrom, to: nextTo },
      }),
    );

    onCancel();
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      <Typography mt="34px" variant="h1">
        Add Missing Exchange Rate
      </Typography>

      <Box className={classes.basicCurrencyDetailsContainer}>
        <Typography variant="h2" ml="24px">
          Exchange Rate Details
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            backgroundColor: '#FFF8E1',
            border: '1px solid #FFE082',
            borderRadius: '6px',
            padding: '10px 12px',
            margin: '12px 24px 24px 24px',
          }}
        >
          <WarningAmberIcon sx={{ color: 'warning.main', mt: '2px' }} fontSize="small" />
          <Typography variant="body2" color="warning.dark">
            Please select the date range for which you want to add missing exchange rates. The system will fetch the
            exchange rates for the selected timeframe from the currency-layer API and add them to the system if they are
            missing.
          </Typography>
        </Box>

        <GridContainer rowSpacing={4} columnSpacing={6} mt={0.5}>
          <GridItem item xs={12} md={6}>
            <Datepicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              inputFormat="YYYY-MM-DD"
              minDate={minSelectableDate}
              maxDate={maxSelectableDate}
              error={Boolean(errors.startDate)}
              helperText={errors.startDate}
              required
            />
          </GridItem>

          <GridItem item xs={12} md={6}>
            <Datepicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              inputFormat="YYYY-MM-DD"
              minDate={minSelectableDate}
              maxDate={maxSelectableDate}
              error={Boolean(errors.endDate)}
              helperText={errors.endDate}
              required
            />
          </GridItem>

          {errors.api ? (
            <GridItem item xs={12}>
              <Typography variant="body2" color="error">
                {errors.api}
              </Typography>
            </GridItem>
          ) : null}
        </GridContainer>

        <Stack direction="row" spacing={2} margin="52px 24px 0px 24px" alignItems="center">
          <Button onClick={onApply} variant="contained" disabled={isApplying}>
            {isApplying ? 'APPLYING...' : 'APPLY'}
          </Button>
          <Button onClick={onCancel} disabled={isApplying}>
            CANCEL
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default AddUpdateCurrencyRate;
